import { useState, useEffect, useCallback, useRef } from 'react';
import { useDebounce } from '@shared/hooks/useDebounce';
import FileUpload from '@/shared/components/FileUpload';
import { FileType } from '@/shared/components/FileUpload/constants';
import { Textarea, TextField } from '@/shared/components/TextField';
import { Button } from '@/shared/components/Buttons';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { DatePickerField } from '@/shared/components/DatePickerField';
import CreateModal from '@/shared/layout/createLayout/CreateModal';
import { EQUIPMENT_LABELS } from '../../constants';
import { useDatePickerScrollClose } from '../../hooks/useDatePickerScrollClose';
import {
  listEquipmentCategories,
  type EquipmentCategoryItem,
} from '../../api/listEquipmentCategoriesApiService';
import type {
  EquipmentEntry,
  EquipmentEntryErrors,
} from '../../types/addEquipmentType';

interface ExistingFileEntry {
  filename: string;
  fileSize: number;
  path: string;
}

interface AddEquipmentFormProps {
  entry: EquipmentEntry;
  errors: EquipmentEntryErrors;
  isSubmitting: boolean;
  mode?: 'add' | 'edit';
  equipmentId?: string;
  initialCategoryLabel?: string;
  uploadProgress?: Record<string, number> | undefined;
  failedUploadFiles?: string[] | undefined;
  existingImageFiles?: ExistingFileEntry[] | undefined;
  existingDocFiles?: ExistingFileEntry[] | undefined;
  onExistingImageDelete?: (() => void) | undefined;
  onExistingDocDelete?: ((path: string) => void) | undefined;
  onFieldChange: (
    field: keyof Pick<
      EquipmentEntry,
      'name' | 'description' | 'serialNumber' | 'modelName'
    >,
    value: string
  ) => void;
  onCategoryChange: (value: string) => void;
  onDateOfProductionChange: (date: Date | undefined) => void;
  onImageChange: (files: File[]) => void;
  onImageError: (message: string) => void;
  onDocsChange: (files: File[]) => void;
  onDocsError: (message: string) => void;
  onRetry?: ((filename: string) => void) | undefined;
  onSubmit: () => void;
  onCancel: () => void;
  canClose?: boolean;
}

export const AddEquipmentForm = ({
  entry,
  errors,
  isSubmitting,
  mode = 'add',
  equipmentId,
  initialCategoryLabel,
  uploadProgress,
  failedUploadFiles,
  existingImageFiles,
  existingDocFiles,
  onExistingImageDelete,
  onExistingDocDelete,
  onFieldChange,
  onCategoryChange,
  onDateOfProductionChange,
  onImageChange,
  onImageError,
  onDocsChange,
  onDocsError,
  onRetry,
  onSubmit,
  onCancel,
  canClose = true,
}: AddEquipmentFormProps) => {
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const datePickerRef = useDatePickerScrollClose(datePickerOpen, () =>
    setDatePickerOpen(false)
  );
  const [defaultCategories, setDefaultCategories] = useState<
    EquipmentCategoryItem[]
  >([]);
  const [searchCategories, setSearchCategories] = useState<
    EquipmentCategoryItem[]
  >([]);
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryHasMore, setCategoryHasMore] = useState(true);
  const [categoryDefaultLoading, setCategoryDefaultLoading] = useState(false);
  const [categorySearchLoading, setCategorySearchLoading] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const debouncedCategorySearch = useDebounce(categorySearch, 500);
  const submitRequestedRef = useRef(false);

  useEffect(() => {
    const load = async () => {
      setCategoryDefaultLoading(true);
      try {
        const result = await listEquipmentCategories({ page: 1, limit: 10 });
        setDefaultCategories(result.data);
        setCategoryHasMore(result.hasNextPage);
        setCategoryPage(1);
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setCategoryDefaultLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!debouncedCategorySearch) {
      setSearchCategories([]);
      return;
    }
    const search = async () => {
      setCategorySearchLoading(true);
      try {
        const result = await listEquipmentCategories({
          page: 1,
          limit: 10,
          search: debouncedCategorySearch,
        });
        setSearchCategories(result.data);
      } catch (err) {
        console.error('Error searching categories:', err);
      } finally {
        setCategorySearchLoading(false);
      }
    };
    search();
  }, [debouncedCategorySearch]);

  const fetchMoreCategories = useCallback(async () => {
    if (categoryDefaultLoading || !categoryHasMore) {
      return;
    }
    setCategoryDefaultLoading(true);
    const nextPage = categoryPage + 1;
    try {
      const result = await listEquipmentCategories({
        page: nextPage,
        limit: 10,
      });
      setDefaultCategories((prev) => [
        ...prev,
        ...result.data.filter((c) => !prev.some((p) => p.id === c.id)),
      ]);
      setCategoryHasMore(result.hasNextPage);
      setCategoryPage(nextPage);
    } catch (err) {
      console.error('Error fetching more categories:', err);
    } finally {
      setCategoryDefaultLoading(false);
    }
  }, [categoryDefaultLoading, categoryHasMore, categoryPage]);

  useEffect(() => {
    if (!submitRequestedRef.current) {
      return;
    }
    const fieldOrder: Array<keyof EquipmentEntryErrors> = [
      'name',
      'description',
      'category',
      'modelName',
      'serialNumber',
      'dateOfProduction',
      'imageFile',
      'docFiles',
    ];
    const firstErrorField = fieldOrder.find((f) => Boolean(errors[f]));
    if (!firstErrorField) {
      return;
    }
    submitRequestedRef.current = false;
    document
      .querySelector(`[data-field="equipment-${firstErrorField}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [errors]);

  const handleCategorySearch = useCallback(
    (q: string) => {
      const trimmed = q.trim();
      if (trimmed === categorySearch) {
        return;
      }
      setCategorySearch(trimmed);
      if (!trimmed) {
        setSearchCategories([]);
        setCategorySearchLoading(false);
      } else {
        // Set here, not in the effect — useDebounce settling triggers a re-render before
        // the watching useEffect runs, leaving a gap where loading=false causes a flicker.
        setCategorySearchLoading(true);
      }
    },
    [categorySearch]
  );

  const displayedCategories = categorySearch
    ? searchCategories
    : defaultCategories;

  const selectedCategoryLabel =
    defaultCategories.find((o) => o.id === entry.category)?.label ??
    searchCategories.find((o) => o.id === entry.category)?.label ??
    initialCategoryLabel;

  // categorySearch !== debouncedCategorySearch covers the debounce window as a safety net.
  const categoryItems =
    (categorySearchLoading || categorySearch !== debouncedCategorySearch) &&
    categorySearch
      ? [{ id: '__loading__', label: 'Ładowanie...', disabled: true }]
      : categoryDefaultLoading && !categorySearch
        ? [
            ...displayedCategories.map((o) => ({
              id: o.id,
              label: o.label,
              toolTip: false,
            })),
            { id: '__loading__', label: 'Ładowanie...', disabled: true },
          ]
        : !categorySearchLoading &&
            categorySearch &&
            displayedCategories.length === 0
          ? [
              {
                id: '__no_results__',
                label: 'Nie znaleziono wyników',
                disabled: true,
              },
            ]
          : displayedCategories.map((o) => ({
              id: o.id,
              label: o.label,
              toolTip: false,
            }));

  const isDirty =
    mode === 'edit' ||
    entry.name !== '' ||
    entry.description !== '' ||
    entry.category !== '' ||
    entry.serialNumber !== '' ||
    entry.dateOfProduction !== undefined ||
    entry.modelName !== '' ||
    entry.imageFile !== undefined ||
    entry.docFiles.length > 0;

  const isUploading = Object.keys(uploadProgress ?? {}).length > 0;

  const handleCancel = () => {
    if (isDirty) {
      setConfirmClearOpen(true);
    } else {
      onCancel();
    }
  };

  const optionalLabel = (text: string) => (
    <>
      {text}
      <span className="text-bodySmall font-normal">
        {' '}
        {EQUIPMENT_LABELS.SECTION_OPTIONAL}
      </span>
    </>
  );

  return (
    <div className="flex flex-col gap-6">
      <CreateModal
        open={confirmClearOpen}
        onClose={() => setConfirmClearOpen(false)}
        onConfirmCancel={() => {
          setConfirmClearOpen(false);
          onCancel();
        }}
      />
      <div className="border border-primary-color-30 p-4 rounded-2xl flex flex-col gap-6">
        <div className="flex flex-col gap-8">
          {/* Title */}
          <p className="text-[18px] font-normal text-neutral-10">
            {mode === 'edit'
              ? EQUIPMENT_LABELS.FORM_TITLE_EDIT
              : EQUIPMENT_LABELS.FORM_TITLE}
          </p>

          {/* Basic Details section */}
          <div className="flex flex-col gap-2">
            <p className="text-titleDefault text-on-surface-variant">
              {EQUIPMENT_LABELS.SECTION_BASIC_DETAILS_HEADING}
            </p>

            <div className="flex flex-col gap-8">
              <div className="flex gap-4">
                <div className="flex-1" data-field="equipment-name">
                  <TextField
                    fullWidth
                    label={EQUIPMENT_LABELS.FIELD_NAME}
                    dataTestId={`equipment-name`}
                    autoTrimMaxLength={200}
                    value={entry.name}
                    onChange={(e) =>
                      onFieldChange('name', e.target.value.trimStart())
                    }
                    error={Boolean(errors.name)}
                    errorMessage={errors.name}
                    disabled={isSubmitting}
                    autoFocus={false}
                  />
                </div>
                {mode === 'edit' && equipmentId ? (
                  <div className="flex-1">
                    <TextField
                      fullWidth
                      label={EQUIPMENT_LABELS.FIELD_EQUIPMENT_ID}
                      value={equipmentId}
                      disabled
                    />
                  </div>
                ) : (
                  <div className="flex-1" />
                )}
                <div className="flex-1" />
              </div>

              <div className="w-full" data-field="equipment-description">
                <Textarea
                  error={!!errors.description}
                  errorMessage={errors.description ?? ''}
                  label="Opis"
                  maxLength={500}
                  width={'100%'}
                  rows={4}
                  value={entry.description}
                  onChange={(e) =>
                    onFieldChange(
                      'description',
                      e.target.value.trimStart().slice(0, 500) || ''
                    )
                  }
                  disabled={isSubmitting}
                  dataTestId={`equipment-description`}
                />
              </div>
            </div>
          </div>

          <div className="border-b border-[#DCDDE0]" />

          {/* Equipment Details section */}
          <div className="flex flex-col gap-2">
            <p className="text-titleDefault text-on-surface-variant">
              {EQUIPMENT_LABELS.SECTION_EQUIPMENT_DETAILS_HEADING}
            </p>

            <div className="flex flex-col gap-8">
              {/* Category | Model Name | Serial Number */}
              <div className="flex gap-4">
                <div className="flex-1 min-w-0" data-field="equipment-category">
                  <Dropdown
                    menuFullWidth
                    menuMaxVisibleItems={4}
                    placement="bottom-start"
                    items={categoryItems}
                    selectedId={entry.category}
                    onItemClick={(id) => {
                      if (id !== '__loading__') {
                        onCategoryChange(id);
                      }
                    }}
                    disabled={isSubmitting}
                    error={Boolean(errors.category)}
                    errorMessage={errors.category}
                    showSearch
                    searchPlaceholder="Szukaj..."
                    onSearchChange={handleCategorySearch}
                    onOpenChange={(open) => {
                      if (!open) {
                        handleCategorySearch('');
                      }
                    }}
                    onScrollEnd={() => {
                      if (
                        !categorySearch &&
                        categoryHasMore &&
                        !categoryDefaultLoading
                      ) {
                        void fetchMoreCategories();
                      }
                    }}
                    trigger={({ open, toggle, disabled: d, error: e }) => (
                      <DropDownButton
                        label={EQUIPMENT_LABELS.FIELD_CATEGORY}
                        value={selectedCategoryLabel ?? ''}
                        open={open}
                        toggle={toggle}
                        disabled={d}
                        error={e}
                        data-testid="equipment-category"
                      />
                    )}
                  />
                </div>

                <div className="flex-1" data-field="equipment-modelName">
                  <TextField
                    fullWidth
                    label={optionalLabel(EQUIPMENT_LABELS.FIELD_MODEL_NAME)}
                    dataTestId={`equipment-model-name`}
                    autoTrimMaxLength={200}
                    value={entry.modelName}
                    onChange={(e) =>
                      onFieldChange('modelName', e.target.value.trimStart())
                    }
                    error={Boolean(errors.modelName)}
                    errorMessage={errors.modelName}
                    disabled={isSubmitting}
                    autoFocus={false}
                  />
                </div>

                <div className="flex-1" data-field="equipment-serialNumber">
                  <TextField
                    fullWidth
                    label={optionalLabel(EQUIPMENT_LABELS.FIELD_SERIAL_NUMBER)}
                    dataTestId={`equipment-serial-number`}
                    autoTrimMaxLength={200}
                    value={entry.serialNumber}
                    onChange={(e) =>
                      onFieldChange('serialNumber', e.target.value.trimStart())
                    }
                    error={Boolean(errors.serialNumber)}
                    errorMessage={errors.serialNumber}
                    disabled={isSubmitting}
                    autoFocus={false}
                  />
                </div>
              </div>

              {/* Date of Production — same width as Category column above */}
              <div className="flex gap-4">
                <div
                  className="flex-1"
                  ref={datePickerRef}
                  data-field="equipment-dateOfProduction"
                >
                  <DatePickerField
                    label={optionalLabel(
                      EQUIPMENT_LABELS.FIELD_DATE_OF_PRODUCTION
                    )}
                    ariaLabel={EQUIPMENT_LABELS.FIELD_DATE_OF_PRODUCTION}
                    pickerLabel={
                      EQUIPMENT_LABELS.FIELD_DATE_OF_PRODUCTION_PICKER_LABEL
                    }
                    dataTestId="equipment-date-of-production"
                    variant="flat"
                    compact
                    open={datePickerOpen}
                    onOpenChange={setDatePickerOpen}
                    value={entry.dateOfProduction}
                    onChange={onDateOfProductionChange}
                    maxDate={new Date()}
                    error={Boolean(errors.dateOfProduction)}
                    errorMessage={errors.dateOfProduction}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="flex-1" />
                <div className="flex-1" />
              </div>
            </div>
          </div>

          <div className="border-b border-[#DCDDE0]" />

          {/* Media & Documents */}
          <div className="flex flex-col gap-4">
            <div
              className="flex flex-col gap-2"
              data-field="equipment-imageFile"
            >
              <p className="text-[18px] text-on-surface-variant">
                {EQUIPMENT_LABELS.SECTION_MEDIA_DOCUMENTS}
              </p>
              <p className="text-bodyLarge text-[#474A4C]">
                {EQUIPMENT_LABELS.FIELD_EQUIPMENT_IMAGE}
              </p>
              <FileUpload
                onFileSelect={onImageChange}
                onError={onImageError}
                selectedFiles={entry.imageFile ? [entry.imageFile] : []}
                {...(existingImageFiles !== undefined && {
                  existingFiles: existingImageFiles,
                })}
                {...(onExistingImageDelete !== undefined && {
                  onExistingFileDelete: (_path: string) => {
                    onExistingImageDelete();
                  },
                })}
                allowedTypes={[FileType.JPG, FileType.JPEG, FileType.PNG]}
                maxFiles={1}
                disabled={isSubmitting}
                error={Boolean(errors.imageFile)}
                errorMessage={errors.imageFile || undefined}
                uploadProgress={
                  entry.imageFile &&
                  uploadProgress?.[`image:${entry.imageFile.name}`] !==
                    undefined
                    ? {
                        [entry.imageFile.name]:
                          uploadProgress[`image:${entry.imageFile.name}`] ?? 0,
                      }
                    : undefined
                }
                failedFiles={failedUploadFiles
                  ?.filter((f) => f.startsWith('image:'))
                  .map((f) => f.slice(6))}
                onRetry={onRetry ? (fn) => onRetry(`image:${fn}`) : undefined}
              />
            </div>

            {/* Safety Documents */}
            <div
              className="flex flex-col gap-2"
              data-field="equipment-docFiles"
            >
              <p className="text-[#474A4C]">
                <span className="text-[16px] leading-[24px] tracking-[0.5px] font-normal">
                  {EQUIPMENT_LABELS.SECTION_SAFETY_DOCUMENTS}
                </span>
                <span className="text-[12px] leading-[16px] tracking-[0.4px] font-normal">
                  {' '}
                  {EQUIPMENT_LABELS.SECTION_OPTIONAL}
                </span>
              </p>
              <FileUpload
                onFileSelect={onDocsChange}
                onError={onDocsError}
                selectedFiles={entry.docFiles}
                {...(existingDocFiles !== undefined && {
                  existingFiles: existingDocFiles,
                })}
                {...(onExistingDocDelete !== undefined && {
                  onExistingFileDelete: onExistingDocDelete,
                })}
                allowedTypes={[
                  FileType.JPG,
                  FileType.JPEG,
                  FileType.PNG,
                  FileType.PDF,
                  FileType.DOCX,
                ]}
                maxFiles={2}
                disabled={isSubmitting}
                error={Boolean(errors.docFiles)}
                errorMessage={errors.docFiles || undefined}
                uploadProgress={
                  uploadProgress
                    ? Object.fromEntries(
                        entry.docFiles
                          .filter(
                            (f) =>
                              uploadProgress[`docs:${f.name}`] !== undefined
                          )
                          .map((f) => [
                            f.name,
                            uploadProgress[`docs:${f.name}`] ?? 0,
                          ])
                      )
                    : undefined
                }
                failedFiles={failedUploadFiles
                  ?.filter((f) => f.startsWith('docs:'))
                  .map((f) => f.slice(5))}
                onRetry={onRetry ? (fn) => onRetry(`docs:${fn}`) : undefined}
              />
            </div>
          </div>

          <div className="border-b-2 border-[#DCDDE0] -mx-4" />

          {/* Action buttons */}
          <div className="flex justify-end gap-4">
            <Button
              label={EQUIPMENT_LABELS.CANCEL_BUTTON}
              buttonType="Square"
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting || (!isDirty && !canClose)}
              className="!bg-transparent disabled:!bg-neutral-10/15 !px-3 !py-1.5  cursor-pointer border border-[#DCDDE0] !text-labelLarge !text-on-surface-variant disabled:!text-neutral-10/[0.38] hover:!border-[#DCDDE0] hover:!bg-[#474A4C]/[0.08] !rounded-[8px]"
            />
            <Button
              label={
                isSubmitting
                  ? mode === 'edit'
                    ? EQUIPMENT_LABELS.SUBMIT_BUTTON_LOADING_EDIT
                    : EQUIPMENT_LABELS.SUBMIT_BUTTON_LOADING
                  : mode === 'edit'
                    ? EQUIPMENT_LABELS.SUBMIT_BUTTON_EDIT
                    : EQUIPMENT_LABELS.SUBMIT_BUTTON
              }
              type="button"
              onClick={() => {
                submitRequestedRef.current = true;
                onSubmit();
              }}
              disabled={isSubmitting || isUploading || !isDirty}
              className="!px-3 !py-1.5  text-labelLarge cursor-pointer !rounded-[8px]"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
